import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PatientApi from '../api/patientApi';
import { PatientCreate, PatientQuery, PatientResponse, PatientUpdate } from '@/schema/patient';
import { PageParam, PageResponse } from '@/schema/page';
import usePatientStore from '@patientMenu/hooks/usePatientStore';
import { useNavigate } from 'react-router-dom';

// Get a single patient by ID
export const usePatient = (id: string, options?: { enabled?: boolean }) => {
  return useQuery<PatientResponse>({
    queryKey: ['patient', id],
    queryFn: () => PatientApi.getById(id),
    enabled: options?.enabled ?? Boolean(id),
  });
};

// Get all patients (paginated)
export const usePatients = (query: PatientQuery, pageParam: PageParam) => {
  return useQuery<PageResponse<PatientResponse>>({
    queryKey: ['patients', query, pageParam.page, pageParam.size],
    queryFn: () => PatientApi.getAll(query, pageParam),
  });
};

// Search all patients (admin only)
export const useSearchAllPatients = (query: string, pageParam: PageParam) => {
  return useQuery<PageResponse<PatientResponse>>({
    queryKey: ['patientsSearchAll', query, pageParam.page, pageParam.size],
    queryFn: () => PatientApi.searchAll(query, pageParam),
  });
};

// Create patient
export const useCreatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PatientCreate) => PatientApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};

// Update patient
export const useUpdatePatient = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PatientUpdate) => PatientApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', id] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};

// Delete patient. `redirectTo: null` keeps the caller where it is — the admin console
// deletes from a side panel and has no clinic list to fall back to.
export const useDeletePatient = (options?: { redirectTo?: string | null }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const redirectTo = options?.redirectTo === undefined ? '/patients' : options.redirectTo;

  return useMutation({
    mutationFn: (id: string) => PatientApi.delete(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ['patient', id] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      if (redirectTo) navigate(redirectTo);
    },
  });
};

// Delete multiple patients
export const useDeletePatients = () => {
  const queryClient = useQueryClient();
  const { setSelectedPatients } = usePatientStore();
  const navigate = useNavigate();

  return useMutation<void, Error, string[]>({
    mutationFn: (ids: string[]) => PatientApi.deletePatients(ids),

    onSuccess: (_, ids) => {
      ids.forEach((id) => {
        queryClient.removeQueries({ queryKey: ['patient', id] });
      });

      queryClient.setQueryData<PageResponse<PatientResponse>>(['patients'], (old) =>
        old
          ? {
              ...old,
              content: old.content.filter((patient) => !ids.includes(patient.id)),
            }
          : old,
      );
      setSelectedPatients([]);
      navigate('/patients');
    },
  });
};
